// Documentation Content for HeyIntake Self-Serve Docs
// Each article has: id, title, category, readTime, content (markdown-style)

export const docsContent = {
  // ============================================
  // GETTING STARTED
  // ============================================
  "creating-your-account": {
    id: "creating-your-account",
    title: "Creating your account",
    category: "getting-started",
    readTime: "2 min",
    content: `
## Creating Your HeyIntake Account

Getting started with HeyIntake takes just a few minutes. Here's how to create your account and start capturing leads.

### Step 1: Sign Up

1. Visit [heyintake.com](/) and click **Get Started** or **Start Trial**
2. Enter your email address and create a secure password
3. Alternatively, click **Continue with Google** for faster signup

### Step 2: Complete Your Profile

After signing up, you'll be prompted to enter:

- **Business Name** — This appears on your intake forms
- **Notification Email** — Where you'll receive lead alerts

### Step 3: Start Your Trial

Your 7-day trial includes:
- Up to 20 form submissions
- Full access to all features
- AI lead scoring and summaries
- No credit card required to start

### What's Next?

Once your account is created, you'll be taken to your dashboard where you can:
- Create your first intake form
- Customize your form fields
- Share your form link with potential clients

---

**Tip:** Enable email notifications in Settings to get instant alerts when new leads come in.
    `
  },

  "setting-up-your-first-form": {
    id: "setting-up-your-first-form",
    title: "Setting up your first form",
    category: "getting-started",
    readTime: "3 min",
    content: `
## Setting Up Your First Intake Form

Create a professional intake form in minutes using our form builder.

### Accessing the Form Builder

1. From your dashboard, click **Form Builder** in the sidebar
2. You'll see your existing forms (if any) and can create new ones

### Creating a New Form

1. Click **Create New Form** or edit an existing form
2. Choose your form mode:
   - **Pre-booking** — For new lead capture and qualification
   - **Post-booking** — For client onboarding after they've signed

### Customizing Your Form

#### Basic Settings
- **Form Name** — Internal name for your reference
- **Welcome Message** — Greeting shown to visitors
- **Submit Button Text** — Customize the CTA

#### Adding Fields
Your form comes with default fields. You can:
- Toggle fields on/off
- Mark fields as required or optional
- Reorder fields by dragging

#### Common Fields Include:
- Full Name
- Email Address
- Phone Number
- Company Name
- Project Description
- Budget Range
- Timeline

### Saving Your Form

Click **Save Form** to publish your changes. Your form is now ready to share!

---

**Pro Tip:** Keep your form concise. Forms with 5-7 fields have the highest completion rates.
    `
  },

  "choosing-pre-booking-vs-post-booking": {
    id: "choosing-pre-booking-vs-post-booking",
    title: "Choosing pre-booking vs post-booking mode",
    category: "getting-started",
    readTime: "4 min",
    content: `
## Pre-Booking vs Post-Booking: Which Mode to Use?

HeyIntake offers two distinct modes for different stages of your client journey. With our **Unified Opportunity Model**, these modes work together seamlessly on the same record.

### Pre-Booking Mode (Lead Capture)

**Use this when:** You're capturing new leads who haven't committed yet.

**What it does:**
- AI scores leads 0-100 based on fit and intent
- Assigns priority labels (Immediate, High, Medium, Low)
- Generates summaries highlighting key details
- Lead Pipeline: New → Contacted → Qualified → Won/Lost

**Best for:**
- Contact forms on your website
- Lead forms for services
- Quote request forms
- Initial consultations

### Post-Booking Mode (Client Onboarding)

**Use this when:** Someone has already agreed to work with you.

**What it does:**
- Collects detailed project requirements
- AI analyzes intake completeness (Clarity Score)
- Identifies missing information and risk flags
- Intake Pipeline: Awaiting Intake → Intake Requested → Received → In Review → Scheduled → In Service → Completed

**Best for:**
- New client onboarding
- Project kickoff questionnaires
- Service intake forms
- Gathering requirements before starting work

### The Unified Opportunity Model

HeyIntake uses a **single record** to track both lead and intake stages. When you mark a lead as "Won" and start intake:

1. **Lead Stage** stays visible (Won) for pipeline reporting
2. **Intake Stage** starts tracking the onboarding workflow
3. **Both stages** appear simultaneously on submission cards
4. **Activity Timeline** shows the complete history

This means:
- No duplicate records
- Clear audit trail of who did what, when
- Accurate reporting across both pipelines
- Seamless handoff from sales to onboarding

### When a Lead Becomes a Client

1. Mark the lead as **Won**
2. Choose whether to **Send Intake Email** immediately
3. Select which intake form to use
4. The submission now shows both statuses:
   - Lead: Won
   - Intake: Intake Requested (or Awaiting Intake)

---

**Recommendation:** Start with a pre-booking form for lead capture. When leads convert, use the "Mark as Won & Start Intake" feature to seamlessly transition them to onboarding.
    `
  },

  "customizing-form-fields": {
    id: "customizing-form-fields",
    title: "Customizing form fields",
    category: "getting-started",
    readTime: "5 min",
    content: `
## Customizing Your Form Fields

Tailor your intake form to collect exactly the information you need.

### Field Types Available

| Field Type | Description | Example |
|------------|-------------|---------|
| Text | Short text input | Name, Company |
| Email | Email with validation | Contact email |
| Phone | Phone number | Mobile/work number |
| Textarea | Long text input | Project description |
| Select | Dropdown options | Budget range |
| Number | Numeric input | Team size |

### Managing Fields

#### Enabling/Disabling Fields
- Toggle the switch next to any field to show or hide it
- Disabled fields won't appear on your public form

#### Required vs Optional
- Check "Required" to make a field mandatory
- Optional fields show "(Optional)" label to visitors

#### Reordering Fields
- Drag and drop fields to change their order
- Put your most important questions first

### Field Configuration

For each field, you can set:
- **Label** — The question text shown to visitors
- **Placeholder** — Helper text inside the field
- **Required** — Whether the field must be filled

### Recommended Field Order

1. **Contact Info First**
   - Full Name (required)
   - Email (required)
   - Phone (optional)

2. **Project Details Second**
   - Company Name
   - Project Description
   - Timeline

3. **Qualification Last**
   - Budget Range
   - How did you hear about us?

### Tips for Better Completion Rates

- Keep required fields to a minimum (3-5)
- Use clear, specific labels
- Provide helpful placeholder text
- Group related questions together
- Test your form on mobile devices

---

**Note:** Changes to form fields take effect immediately after saving.
    `
  },

  "sharing-your-intake-form": {
    id: "sharing-your-intake-form",
    title: "Sharing your intake form",
    category: "getting-started",
    readTime: "2 min",
    content: `
## Sharing Your Intake Form

Once your form is ready, share it with potential clients using the unique form link.

### Finding Your Form Link

1. Go to **Form Builder** in the sidebar
2. Your form URL is displayed at the top
3. Click **Copy Link** to copy it to your clipboard

### Your Form URL Structure

Your form link looks like:
\`\`\`
https://yoursite.com/intake/abc123
\`\`\`

Each form has a unique slug that identifies it.

### Where to Share Your Form

#### On Your Website
- Add a "Contact Us" or "Get a Quote" button
- Link to your form from your services page
- Embed the link in your website footer

#### In Email Signatures
Add a line like:
> "Ready to work together? [Submit your project details here](link)"

#### On Social Media
Share the link in:
- LinkedIn posts or profile
- Twitter/X bio
- Instagram link in bio

#### In Client Communications
Include in:
- Proposal documents
- Welcome emails
- Booking confirmations

### Direct Link vs QR Code

For in-person events or printed materials, you can:
1. Generate a QR code from your form URL
2. Use any free QR code generator
3. Print it on business cards or flyers

---

**Tip:** Bookmark your form link for easy access when sharing with clients.
    `
  },

  // ============================================
  // CRM & PIPELINE
  // ============================================
  "understanding-the-pipeline-view": {
    id: "understanding-the-pipeline-view",
    title: "Understanding the pipeline view",
    category: "crm-pipeline",
    readTime: "4 min",
    content: `
## Understanding the Pipeline View

The pipeline view gives you a clear overview of all your leads and their current status.

### Accessing the Pipeline

Click **Submissions** in the sidebar to see your leads. Use the tabs to filter:
- **All** — Every submission
- **Leads** — Pre-booking submissions
- **Intakes** — Post-booking submissions

### Pipeline Columns

Each submission shows:

| Column | Description |
|--------|-------------|
| AI Score | 0-100 quality score (color-coded) |
| Name/Company | Submitter identity |
| Status | Current pipeline stage |
| Submitted | Date and time |
| Form | Which form they used |

### Sorting & Filtering

- **Click column headers** to sort
- **Use search** to find specific leads
- **Filter by status** to focus on specific stages
- **Filter by date** to see recent submissions

### Color-Coded Scores

AI scores are color-coded for quick scanning:
- 🟢 **80-100** — Hot lead, immediate attention
- 🟡 **60-79** — Warm lead, follow up soon
- 🟠 **40-59** — Medium priority
- 🔴 **0-39** — Lower priority

### Viewing Lead Details

Click any row to open the full submission details:
- All form responses
- AI-generated summary
- Notes and activity history
- Status change options

### Quick Actions

From the pipeline view, you can:
- Change status via dropdown
- Add quick notes
- Set reminders
- Export to CSV

---

**Pro Tip:** Check your pipeline daily and focus on high-scoring leads first.
    `
  },

  "lead-status-workflow-pre-booking": {
    id: "lead-status-workflow-pre-booking",
    title: "Lead status workflow (Pre-booking)",
    category: "crm-pipeline",
    readTime: "3 min",
    content: `
## Lead Status Workflow (Pre-Booking)

Pre-booking leads move through a qualification pipeline as you evaluate and convert them.

### Status Stages

#### 1. New
- Lead just submitted the form
- Hasn't been reviewed yet
- Action: Review and assess fit

#### 2. Contacted
- You've reached out to the lead
- Initial conversation started
- Action: Schedule a call or send proposal

#### 3. Qualified
- Lead is a good fit for your services
- Discussing scope and pricing
- Action: Send proposal or contract

#### 4. Won
- Lead converted to a client
- Contract signed or payment received
- Action: Move to post-booking intake (if needed)

#### 5. Lost
- Lead didn't convert
- Not a fit, went elsewhere, or went silent
- Action: Note the reason for future reference

### Changing Status

1. Click on any lead in the pipeline
2. Use the **Status** dropdown in the details panel
3. Select the new status
4. Status change is saved automatically

### Best Practices

- **Move leads promptly** — Update status as conversations progress
- **Don't skip stages** — Track each step for accurate reporting
- **Mark lost leads** — Helps understand your conversion rate
- **Add notes** — Document why status changed

### Automation Ideas

- When a lead scores 80+, prioritize for immediate outreach
- Set a reminder if a lead stays in "Contacted" for 3+ days
- Review "New" leads within 24 hours

---

**Tip:** Your pipeline status helps you understand where leads get stuck in your sales process.
    `
  },

  "intake-status-workflow-post-booking": {
    id: "intake-status-workflow-post-booking",
    title: "Intake status workflow (Post-booking)",
    category: "crm-pipeline",
    readTime: "4 min",
    content: `
## Intake Status Workflow (Post-Booking)

Post-booking intakes track client onboarding from request to active engagement. With HeyIntake's **Unified Model**, intake tracking works seamlessly alongside lead tracking on the same record.

### Intake Stages

#### 1. Awaiting Intake
- Lead marked as Won but intake email not sent yet
- Ready to send intake form when you're ready
- Action: Send intake email or schedule onboarding call

#### 2. Intake Requested
- Intake form link sent to client via email
- **Secure token** generated (expires in 14 days)
- Waiting for client to complete
- Action: Send reminder if not completed in 2-3 days

#### 3. Intake Received
- Client submitted their intake form
- **You receive an email notification automatically**
- Ready for your review
- Action: Review completeness and follow up on gaps

#### 4. Client Action Required
- Intake submitted but missing information
- AI flagged gaps or required documents
- Action: Reach out for clarification

#### 5. In Review
- Actively reviewing the intake details
- May need clarification from client
- Action: Reach out with questions if needed

#### 6. Scheduled
- Intake reviewed and approved
- Project or session scheduled
- Action: Confirm dates and prepare

#### 7. In Service
- Work has begun
- Client is actively engaged
- Action: Deliver your service

#### 8. Completed
- Project or engagement finished
- All deliverables provided
- Action: Request feedback or testimonial

### Dual Status Display

When a lead is marked Won and intake starts, you'll see **both statuses** on the submission card:

| Lead Stage | Intake Stage |
|------------|--------------|
| Won | Intake Requested |
| Won | Intake Received |
| Won | In Service |

This ensures:
- Complete visibility into where each client is
- Lead conversion metrics remain accurate
- Intake progress is clearly tracked

### Activity Timeline

Every status change is logged in the **Activity Timeline**, showing:
- Who made the change
- When it happened
- Before and after states
- Email sent events

### Secure Intake Links

When you send an intake email:
- A **secure token** is generated (256-bit cryptographic)
- Token expires after **14 days**
- Token is **one-time use** (can't be reused after submission)
- Previous tokens are **invalidated** when you resend

### Email Notifications

You receive automatic email notifications when:
- A client submits their intake form
- A submission moves to "Client Action Required"
- AI flags potential issues

---

**Tip:** Check the Activity Timeline in any submission to see the complete history of who did what, when.
    `
  },

  "activity-timeline-audit-trail": {
    id: "activity-timeline-audit-trail",
    title: "Activity Timeline & audit trail",
    category: "crm-pipeline",
    readTime: "3 min",
    content: `
## Activity Timeline & Audit Trail

Every submission includes a complete Activity Timeline showing who did what, when. This provides full accountability and makes it easy to understand the history of any lead or intake.

### Viewing the Activity Timeline

1. Open any submission from the Submissions page
2. Scroll to the **Activity Timeline** section
3. See chronological history with most recent first

### Events Tracked

| Event | Description |
|-------|-------------|
| Submission Created | When the lead/intake was first created |
| Lead Stage Changed | When lead status was updated (e.g., New → Contacted) |
| Intake Stage Changed | When intake status was updated (e.g., Requested → Received) |
| Intake Email Sent | When an intake request email was sent |
| Intake Form Selected | When an intake form was assigned |
| Intake Received | When a client submitted their intake form |

### What Each Entry Shows

Every timeline entry includes:
- **Action** — What happened
- **Timestamp** — When it happened
- **Actor** — Who did it (user, client, or system)
- **Details** — Context like from→to for status changes

### Example Timeline

    📋 Intake Stage Changed
       Intake Requested → Intake Received
       Feb 27, 2026 2:15 PM by client

    ✉️ Intake Email Sent
       To: sarah@example.com
       Feb 27, 2026 10:30 AM by user

    🎯 Lead Stage Changed
       Qualified → Won
       Feb 27, 2026 10:28 AM by user

    ➕ Submission Created
       Source: intake_first
       Feb 27, 2026 10:00 AM by user

### Use Cases

- **Accountability** — Know who changed what and when
- **Debugging** — Understand why something is in a certain state
- **Handoffs** — New team members can see full history
- **Compliance** — Audit trail for regulated industries

### Retention

Activity logs are stored permanently with the submission. They cannot be edited or deleted, ensuring a complete audit trail.

---

**Pro Tip:** When handing off a client internally, point team members to the Activity Timeline for full context.
    `
  },

  "adding-notes-and-comments": {
    id: "adding-notes-and-comments",
    title: "Adding notes and internal comments",
    category: "crm-pipeline",
    readTime: "2 min",
    content: `
## Adding Notes and Internal Comments

Keep track of conversations and context with internal notes on each submission.

### Adding a Note

1. Click on any lead/intake to open the details panel
2. Scroll to the **Notes** section
3. Type your note in the text field
4. Click **Add Note** to save

### What to Include in Notes

Good notes capture:
- Key points from conversations
- Next steps agreed upon
- Important dates or deadlines
- Reasons for status changes
- Client preferences or concerns

### Note Examples

> "Spoke with Sarah on 12/15. She needs the project done by end of Q1. Budget approved, sending proposal tomorrow."

> "Left voicemail. Will try again Thursday."

> "Lost to competitor - they offered lower price. Good fit otherwise, keep for future."

### Notes vs Submission Data

| Notes | Submission Data |
|-------|-----------------|
| Your internal observations | Client's form responses |
| Can be edited/deleted | Read-only after submission |
| Only visible to your team | Original client input |

### Team Collaboration

If you have team members:
- All notes are visible to your team
- Each note shows who added it and when
- Use @mentions to notify teammates (coming soon)

---

**Tip:** Add a note every time you interact with a lead. Your future self will thank you.
    `
  },

  "using-tags-to-organize": {
    id: "using-tags-to-organize",
    title: "Using tags to organize leads",
    category: "crm-pipeline",
    readTime: "2 min",
    content: `
## Using Tags to Organize Leads

Tags help you categorize and filter leads based on any criteria you choose.

### Adding Tags

1. Open any lead/intake details
2. Find the **Tags** section
3. Click to add or create tags
4. Tags are saved automatically

### Tag Ideas by Category

#### By Service Type
- Website Design
- Branding
- Consulting
- Retainer

#### By Source
- Website
- Referral
- LinkedIn
- Event

#### By Priority
- VIP
- Enterprise
- Small Business
- Follow-Up Needed

#### By Industry
- Tech
- Healthcare
- E-commerce
- Non-Profit

### Filtering by Tag

1. Go to Submissions page
2. Use the filter dropdown
3. Select one or more tags
4. View only matching leads

### Tag Best Practices

- **Keep it simple** — Don't create too many tags
- **Be consistent** — Use the same tags across your team
- **Review regularly** — Remove unused tags
- **Combine with status** — Tags + status = powerful filtering

### Example Workflow

1. New lead comes in → Auto-tagged by form used
2. You review → Add service type tag
3. Qualify the lead → Add "VIP" if high-value
4. Later → Filter by "VIP" + "Contacted" to prioritize follow-ups

---

**Tip:** Tags are most useful when your team agrees on a consistent tagging system.
    `
  },

  "setting-and-managing-reminders": {
    id: "setting-and-managing-reminders",
    title: "Setting and managing reminders",
    category: "crm-pipeline",
    readTime: "3 min",
    content: `
## Setting and Managing Reminders

Never forget to follow up with reminders on any lead or intake.

### Creating a Reminder

1. Open any lead/intake details
2. Click **Set Reminder** or find the reminder section
3. Choose a date and time
4. Add an optional note about what to do
5. Save the reminder

### Reminder Options

- **Quick options**: Tomorrow, In 3 days, Next week
- **Custom date**: Pick any future date
- **With note**: Add context like "Send proposal" or "Follow up on questions"

### Viewing Your Reminders

Reminders appear in:
- Your dashboard overview
- The submission details page
- Email notifications (if enabled)

### Managing Reminders

- **Complete**: Mark as done when you've followed up
- **Snooze**: Push to a later date if needed
- **Delete**: Remove if no longer relevant

### Automated Reminders

For post-booking intakes, HeyIntake can automatically remind clients who haven't completed their intake form. This is configured in Settings.

### When to Set Reminders

| Situation | Suggested Reminder |
|-----------|-------------------|
| Left voicemail | 2-3 days |
| Sent proposal | 5-7 days |
| Waiting for contract | 3 days |
| Client went quiet | 1 week |
| Seasonal follow-up | 30-90 days |

---

**Pro Tip:** Set a reminder immediately after any client interaction so you never lose track.
    `
  },

  // ============================================
  // AI SCORING
  // ============================================
  "how-lead-scoring-works": {
    id: "how-lead-scoring-works",
    title: "How lead scoring works (0-100)",
    category: "ai-scoring",
    readTime: "4 min",
    content: `
## How AI Lead Scoring Works

Every pre-booking submission is automatically analyzed and scored from 0-100.

### What the Score Means

| Score Range | Label | Meaning |
|-------------|-------|---------|
| 80-100 | Immediate | Hot lead, high intent, great fit |
| 60-79 | High | Strong interest, likely to convert |
| 40-59 | Medium | Moderate fit, needs nurturing |
| 0-39 | Low | Poor fit or low intent |

### How Scores Are Calculated

The AI analyzes multiple factors from the submission:

#### Positive Signals (Increase Score)
- Clear project description with specific goals
- Defined budget range
- Urgent timeline
- Decision-maker authority
- Professional email domain
- Complete form responses

#### Negative Signals (Decrease Score)
- Vague or minimal responses
- No budget mentioned
- "Just looking" or research phase
- Generic email (optional)
- Incomplete required fields

### Score Accuracy

The AI learns from patterns in your submissions. Factors weighted include:
- **Intent signals** — How ready are they to buy?
- **Fit signals** — Do they match your ideal client?
- **Urgency** — How soon do they need help?
- **Authority** — Can they make decisions?

### Using Scores Effectively

1. **Prioritize high scores** — Reach out within 24 hours
2. **Don't ignore medium scores** — Many convert with nurturing
3. **Learn from low scores** — Understand why they're not a fit
4. **Track patterns** — See which score ranges convert best

### Score Is Not Everything

The AI score is a starting point. Always:
- Read the full submission
- Check the AI summary for context
- Use your judgment for edge cases

---

**Note:** Scores are calculated instantly when forms are submitted. They don't change unless you trigger a manual recalculation.
    `
  },

  "understanding-priority-labels": {
    id: "understanding-priority-labels",
    title: "Understanding priority labels",
    category: "ai-scoring",
    readTime: "2 min",
    content: `
## Understanding Priority Labels

Each lead is assigned a priority label based on their AI score and urgency.

### Priority Levels

#### Immediate (Red Badge)
- Score: 80-100
- High intent, ready to buy
- Action: Contact within hours
- Example: "Need this done by end of month, budget approved"

#### High (Orange Badge)
- Score: 60-79
- Strong interest, good fit
- Action: Contact within 24 hours
- Example: "Evaluating options, timeline is Q1"

#### Medium (Yellow Badge)
- Score: 40-59
- Moderate interest
- Action: Contact within 2-3 days
- Example: "Exploring possibilities, no rush"

#### Low (Gray Badge)
- Score: 0-39
- Research phase or poor fit
- Action: Nurture or deprioritize
- Example: "Just curious about pricing"

### How Priority Is Determined

Priority combines:
1. **AI Score** — Overall quality assessment
2. **Urgency Signals** — Timeline mentioned
3. **Authority Level** — Decision-maker status

### Changing Priority

Priority labels are auto-assigned but you can override them:
1. Open the lead details
2. Manual priority override (if available)
3. Or use tags to mark VIP leads

### Response Time Matters

Research shows:
- Responding in < 5 minutes = 9x more likely to convert
- After 30 minutes, odds drop significantly

Use priority labels to ensure hot leads get immediate attention.

---

**Tip:** Filter your pipeline by priority to focus on the leads most likely to convert.
    `
  },

  "ai-generated-summaries": {
    id: "ai-generated-summaries",
    title: "AI-generated summaries explained",
    category: "ai-scoring",
    readTime: "3 min",
    content: `
## AI-Generated Summaries Explained

Every submission includes an AI-generated summary to help you quickly understand the lead.

### What's in a Summary

The AI extracts and highlights:

1. **Key Details**
   - Who they are (role, company)
   - What they need (project type)
   - When they need it (timeline)
   - Budget indication

2. **Intent Signals**
   - How serious they are
   - Their decision-making authority
   - Urgency indicators

3. **Recommended Action**
   - Suggested next step
   - Follow-up timing

### Example Summary

> "Decision maker at mid-size tech company seeking website redesign. Clear scope (8-10 pages), defined budget ($15-25K), and urgent timeline (launch in 6 weeks). High intent - recommend immediate outreach to discuss requirements."

### How Summaries Help

| Without Summary | With Summary |
|-----------------|--------------|
| Read entire form | Scan key points in seconds |
| Extract details manually | AI highlights what matters |
| Guess at priority | Clear recommendation provided |

### Summary vs Full Responses

The summary is a starting point. Always review full responses for:
- Nuanced details the AI might miss
- Specific questions to address
- Context that affects your approach

### Email Notifications

When email notifications are enabled, summaries are included in new lead alerts so you can assess leads on the go.

---

**Pro Tip:** Read summaries first, then dive into details for leads you want to pursue.
    `
  },

  "intake-clarity-scoring": {
    id: "intake-clarity-scoring",
    title: "Intake clarity scoring (Post-booking)",
    category: "ai-scoring",
    readTime: "4 min",
    content: `
## Intake Clarity Scoring (Post-Booking)

Post-booking intakes receive a clarity score measuring how complete and actionable the submission is.

### What Clarity Score Measures

Unlike lead scoring (which measures fit), clarity scoring measures:
- **Completeness** — Are all important fields filled?
- **Specificity** — Are responses detailed enough?
- **Actionability** — Can you proceed with this information?

### Score Interpretation

| Clarity Score | Meaning | Action |
|---------------|---------|--------|
| 80-100 | Complete | Ready to proceed |
| 60-79 | Minor gaps | Quick follow-up needed |
| 40-59 | Significant gaps | Discussion required |
| 0-39 | Incomplete | Request re-submission |

### What the AI Analyzes

#### Positive Factors
- All required fields completed
- Detailed project description
- Clear goals and expectations
- Specific timelines provided
- Documents uploaded (if requested)
- Agreement acknowledged

#### Negative Factors
- Empty or minimal responses
- Vague project descriptions
- Missing required information
- No timeline specified
- Documents not provided

### Clarity Score Components

The details panel shows:
1. **Overall Score** — Single 0-100 number
2. **Missing Fields** — What's incomplete
3. **Missing Documents** — Files not uploaded
4. **Risk Flags** — Potential issues identified
5. **Suggested Follow-ups** — Questions to ask

### Using Clarity Scores

1. **High scores (80+)**: Proceed with onboarding
2. **Medium scores (60-79)**: Send quick clarification questions
3. **Low scores (<60)**: Schedule a call to gather information

---

**Tip:** Clients appreciate when you ask smart follow-up questions rather than starting with incomplete info.
    `
  },

  "risk-flags-and-followups": {
    id: "risk-flags-and-followups",
    title: "Risk flags and follow-up suggestions",
    category: "ai-scoring",
    readTime: "3 min",
    content: `
## Risk Flags and Follow-Up Suggestions

The AI identifies potential issues and recommends questions to clarify scope.

### What Are Risk Flags?

Risk flags highlight concerns that could affect project success:

#### Common Risk Flags

| Flag | What It Means |
|------|---------------|
| Unclear scope | Project description is vague |
| Tight timeline | Deadline may be unrealistic |
| Budget mismatch | Expectations may not align with budget |
| Missing stakeholders | Other decision-makers not mentioned |
| Scope creep risk | Request is open-ended |

### How to Use Risk Flags

1. **Review before starting** — Address flags in kickoff
2. **Ask clarifying questions** — Use suggested follow-ups
3. **Document agreements** — Get clarity in writing
4. **Set expectations** — Discuss concerns openly

### Suggested Follow-Up Questions

The AI generates questions based on gaps detected:

> "You mentioned a tight timeline. Can you confirm the firm deadline and if there's any flexibility?"

> "The project scope is broad. Could you prioritize the must-have features vs nice-to-haves?"

> "Will there be other stakeholders reviewing deliverables? Who should be included in feedback rounds?"

### When to Raise Concerns

Some risk flags warrant deeper discussion:
- **Budget significantly below scope** — Have a frank conversation
- **Timeline is impossible** — Negotiate before committing
- **Vague requirements** — Discovery session needed

### Risk Flags in Exports

When you export data to CSV, risk flags are included so you can analyze patterns across submissions.

---

**Remember:** Risk flags are meant to help you succeed. Addressing them early prevents problems later.
    `
  },

  // ============================================
  // AGREEMENTS
  // ============================================
  "setting-up-agreement-links": {
    id: "setting-up-agreement-links",
    title: "Setting up agreement links",
    category: "agreements",
    readTime: "3 min",
    content: `
## Setting Up Agreement Links

Require clients to review and acknowledge your terms before submitting an intake form.

### What You'll Need

Before setup, prepare:
- Your agreement document (PDF, webpage, or DocuSign link)
- A publicly accessible URL to the agreement
- Optional instructions for clients

### Configuration Steps

1. Go to **Settings** in the sidebar
2. Scroll to **Agreement Handling** section
3. Enter your **Agreement Link** (must start with https://)
4. Add **Instructions** (optional, up to 500 characters)
5. Click **Save Agreement Settings**

### Agreement Link Examples

Your agreement link can point to:
- **PDF file**: \`https://yourdomain.com/agreement.pdf\`
- **Google Doc**: \`https://docs.google.com/document/d/xxx/view\`
- **DocuSign**: \`https://docusign.com/xxx\`
- **Website page**: \`https://yourdomain.com/terms\`

### Writing Instructions

Good instructions are clear and brief:

> "Please review our Service Agreement before submitting. By checking the box below, you confirm you've read and agree to these terms."

> "Click the link to view our project terms and conditions. This covers scope, timeline, and payment expectations."

### What Clients See

When agreement is required, clients see:
1. A "Review Agreement" button (opens your link)
2. Your instructions (if provided)
3. A checkbox: "I have reviewed and agree to the terms"

The checkbox must be checked before they can submit.

---

**Important:** The agreement link you set is snapshotted per submission. If you update the link later, past submissions still reference the original URL.
    `
  },

  "adding-agreement-instructions": {
    id: "adding-agreement-instructions",
    title: "Adding agreement instructions",
    category: "agreements",
    readTime: "2 min",
    content: `
## Adding Agreement Instructions

Custom instructions help clients understand what they're agreeing to.

### Where Instructions Appear

Instructions are shown:
- Above the "Review Agreement" button
- Only when agreement is required on a form

### Writing Effective Instructions

Keep instructions:
- **Clear** — State exactly what the document is
- **Brief** — Under 500 characters
- **Actionable** — Tell them what to do

### Examples by Use Case

#### Service Agreement
> "Please review our Service Agreement covering project scope, deliverables, timelines, and payment terms. Click the button to view the full document."

#### Terms of Service
> "By proceeding, you agree to our Terms of Service and Privacy Policy. Please review before continuing."

#### Contract Preview
> "This is a preview of our standard contract. The final version will be sent for signature after we confirm project details."

#### Non-Disclosure Agreement
> "Before we discuss project specifics, please review and acknowledge our mutual NDA."

### Instructions vs Agreement Content

| Instructions | Agreement Document |
|--------------|-------------------|
| Brief context | Full legal terms |
| Shown in form | Opens in new tab |
| 500 char limit | No limit |

### When to Update Instructions

Update your instructions when:
- You change your agreement document
- Clients express confusion
- Your service offering changes

---

**Tip:** Test how instructions look by previewing your form before sharing.
    `
  },

  "enabling-per-form-agreement": {
    id: "enabling-per-form-agreement",
    title: "Enabling per-form agreement requirement",
    category: "agreements",
    readTime: "2 min",
    content: `
## Enabling Per-Form Agreement Requirement

Control which forms require agreement acknowledgment.

### Why Per-Form Control?

Different forms may have different needs:
- Lead form → No agreement needed
- Client onboarding → Agreement required
- Quote request → Maybe optional

### Enabling Agreement on a Form

1. Go to **Form Builder**
2. Select the form you want to edit
3. Find the **Agreement** section
4. Toggle **"Require Agreement"** on
5. Save your form

### Requirements Check

For agreement to work, you need:
1. ✅ Agreement link configured in Settings
2. ✅ "Require Agreement" enabled on the form

If the link isn't configured but the toggle is on, a warning will appear.

### Agreement Flow

When enabled, clients must:
1. Click "Review Agreement" (opens your link)
2. Read the agreement document
3. Check the acknowledgment box
4. Then submit the form

### What If They Don't Acknowledge?

The submit button won't work until:
- The checkbox is checked
- All other required fields are filled

### Forms Without Agreement

For forms where agreement isn't enabled:
- No agreement section appears
- Submission works normally
- No agreement data is recorded

---

**Note:** Agreement settings are saved per-form. Changes to one form don't affect others.
    `
  },

  "viewing-agreement-status": {
    id: "viewing-agreement-status",
    title: "Viewing agreement status per submission",
    category: "agreements",
    readTime: "2 min",
    content: `
## Viewing Agreement Status Per Submission

Track who acknowledged your agreement and when.

### Agreement Status Indicators

Each submission shows agreement status in the details panel:

| Status | Meaning |
|--------|---------|
| ✓ Acknowledged | Client reviewed and agreed |
| Pending | Required but not completed |
| Not Required | Form didn't require agreement |

### Detailed Agreement Info

For submissions with agreement, you can see:
- **Signed**: Yes/No
- **Signed At**: Timestamp when acknowledged
- **Link Opened**: Yes/No (informational)
- **Opened At**: When they clicked the link
- **Agreement URL**: Link they reviewed

### Finding Agreement Status

1. Open any submission from the Submissions page
2. Look for the **Agreement** row in the details
3. Click "View Agreement" to see the document they reviewed

### Why Track "Link Opened"?

The "Link Opened" field tells you if the client clicked to view your agreement. This is informational only — clicking isn't required to submit.

Useful for:
- Verifying they actually viewed the document
- Understanding client behavior
- Identifying if the link works

### Agreement History

The agreement URL is snapshotted at submission time. Even if you update your Settings later:
- Past submissions keep their original URL
- You can always see what they agreed to

---

**Tip:** Use agreement status to ensure compliance before starting work with a new client.
    `
  },

  "exporting-agreement-data": {
    id: "exporting-agreement-data",
    title: "Exporting agreement data",
    category: "agreements",
    readTime: "2 min",
    content: `
## Exporting Agreement Data

Include agreement information in your CSV exports for record-keeping.

### Agreement Fields in Export

When you export submissions, agreement data includes:

| Column | Description |
|--------|-------------|
| Agreement Required | Yes/No - was it required on the form |
| Agreement Signed | Yes/No - did they acknowledge |
| Agreement Signed At | Timestamp of acknowledgment |
| Agreement Opened | Yes/No - did they click the link |
| Agreement Opened At | When they clicked |
| Agreement URL | Link they reviewed |

### How to Export

1. Go to **Submissions** page
2. Click **Export to CSV**
3. Choose your filters (date range, status, etc.)
4. Download the file

### Using Exported Data

Export agreement data to:
- Keep records for compliance
- Track acknowledgment rates
- Import into your CRM or legal system
- Audit client agreements

### Sample Export Row

\`\`\`
Agreement Required: Yes
Agreement Signed: Yes
Agreement Signed At: 2024-01-15T14:30:00Z
Agreement Opened: Yes
Agreement Opened At: 2024-01-15T14:28:45Z
Agreement URL: https://example.com/agreement.pdf
\`\`\`

### Filtering Agreement Status

Before export, you can filter to show only:
- Submissions with agreement required
- Submissions where agreement was signed
- Specific date ranges

---

**Legal Note:** Exported agreement data can serve as a record of client consent. Consult your legal advisor for compliance requirements.
    `
  },

  // ============================================
  // DOCUMENTS
  // ============================================
  "setting-up-document-upload-links": {
    id: "setting-up-document-upload-links",
    title: "Setting up document upload links",
    category: "documents",
    readTime: "3 min",
    content: `
## Setting Up Document Upload Links

Request files from clients using external upload services.

### How Document Collection Works

HeyIntake doesn't store files directly. Instead:
1. You provide a link to an upload service (Dropbox, Google Drive, etc.)
2. Clients click the link and upload directly to your storage
3. You receive files in your chosen platform

### Configuration Steps

1. Go to **Settings** in the sidebar
2. Find **Document Handling** section
3. Choose your preferred method:
   - None (documents not required)
   - External Upload Link
   - Business Email (clients email you documents)
4. If using External Link, enter your upload URL
5. Add instructions for clients
6. Save settings

### Supported Services

Any file sharing service works:
- **Dropbox File Requests** — Best for organized collection
- **Google Drive Folder** — Share a folder link
- **WeTransfer** — Simple file transfers
- **Custom portal** — Your own upload system

### What Clients See

When documents are required, clients see:
1. Your instructions explaining what to upload
2. An "Upload Documents" button
3. Clicking opens your external upload link

### Upload Link Examples

- Dropbox: \`https://dropbox.com/request/xxxxx\`
- Google Drive: \`https://drive.google.com/drive/folders/xxxxx?usp=sharing\`
- WeTransfer: \`https://wetransfer.com/upload\`

---

**Important:** Files go directly to your storage service. HeyIntake only links to it.
    `
  },

  "using-dropbox-file-requests": {
    id: "using-dropbox-file-requests",
    title: "Using Dropbox file requests",
    category: "documents",
    readTime: "3 min",
    content: `
## Using Dropbox File Requests

Dropbox File Requests provide a clean way to collect documents from clients.

### Why Dropbox File Requests?

- **No Dropbox account needed** — Clients can upload without signing up
- **Organized storage** — Files go to a designated folder
- **Professional experience** — Clean upload interface
- **Notifications** — Get notified when files arrive

### Creating a File Request

1. Log into Dropbox
2. Go to **File requests** (or dropbox.com/requests)
3. Click **Create request**
4. Name your request (e.g., "Client Documents")
5. Choose destination folder
6. Set optional deadline
7. Copy the generated link

### Adding to HeyIntake

1. Go to **Settings** > **Document Handling**
2. Select "External Upload Link"
3. Paste your Dropbox File Request link
4. Add instructions like: "Please upload your brand guidelines, logos, and any reference materials."
5. Save settings

### Client Experience

When clients click "Upload Documents":
1. Dropbox upload page opens in new tab
2. They drag/drop or browse for files
3. No login required
4. Files appear in your Dropbox folder

### Tips for File Requests

- **Name clearly** — Include client name in request title
- **Set deadlines** — Optional but helpful
- **Create per-project** — For better organization
- **Check regularly** — You'll get email notifications

---

**Note:** Dropbox File Requests require a Dropbox account on your end (free works for basic use).
    `
  },

  "using-google-drive-for-uploads": {
    id: "using-google-drive-for-uploads",
    title: "Using Google Drive for uploads",
    category: "documents",
    readTime: "3 min",
    content: `
## Using Google Drive for Uploads

Share a Google Drive folder where clients can add their files.

### Setting Up a Shared Folder

1. Open Google Drive
2. Create a new folder (e.g., "Client Uploads")
3. Right-click the folder
4. Click **Share**
5. Under "General access," set to "Anyone with the link"
6. Change permission to **Editor** (allows uploads)
7. Copy the link

### Adding to HeyIntake

1. Go to **Settings** > **Document Handling**
2. Select "External Upload Link"
3. Paste your Google Drive folder link
4. Add instructions explaining what to upload
5. Save settings

### Client Experience

When clients click "Upload Documents":
1. Google Drive folder opens
2. They see existing files (if any)
3. Can drag/drop or use "New" button to upload
4. May need Google account for some features

### Folder Organization Tips

- **Create subfolders** — One per client or project
- **Name clearly** — Include dates or client names
- **Review permissions** — Make sure uploads work
- **Clean up regularly** — Move files to permanent storage

### Google Drive vs Dropbox

| Feature | Google Drive | Dropbox File Request |
|---------|--------------|---------------------|
| Account required | Sometimes | No |
| Upload interface | Standard Drive | Dedicated upload page |
| Organization | Manual folders | Auto-organized |
| Free storage | 15 GB | 2 GB |

### Troubleshooting

**Client can't upload?**
- Check folder is set to "Anyone with link" + "Editor"
- They may need to sign in to Google

**Files not appearing?**
- Refresh your Drive
- Check spam folder for notifications

---

**Tip:** For frequent document collection, Dropbox File Requests offer a cleaner client experience.
    `
  },

  "adding-upload-instructions": {
    id: "adding-upload-instructions",
    title: "Adding upload instructions for clients",
    category: "documents",
    readTime: "2 min",
    content: `
## Adding Upload Instructions for Clients

Clear instructions help clients upload the right files.

### Why Instructions Matter

Without instructions, clients might:
- Upload wrong file formats
- Miss important documents
- Not know what you need
- Give up and skip uploads

### Writing Good Instructions

Include:
1. **What files you need** — Be specific
2. **Preferred formats** — PDF, PNG, etc.
3. **File naming** — How to name files
4. **Important notes** — Size limits, deadlines

### Example Instructions

#### For Brand Projects
> "Please upload: (1) Your current logo files (AI, EPS, or PNG), (2) Brand guidelines if available, (3) 3-5 examples of designs you like. Accepted formats: PDF, PNG, JPG."

#### For Legal/Contracts
> "Upload signed contract and any supporting documents. PDF format preferred. Please name files clearly (e.g., 'Signed_Contract_YourName.pdf')."

#### For Creative Projects
> "Share reference images, mood boards, or examples that inspire you. Any format works - we'll review everything before our kickoff call."

### Where Instructions Appear

Instructions show:
- On your intake form, above the upload button
- Giving clients context before they click

### Instruction Length

Keep it concise:
- 1-3 sentences is ideal
- Use bullet points for multiple items
- Maximum ~500 characters

---

**Tip:** Review your instructions from a client's perspective. Would you know exactly what to do?
    `
  },

  // ============================================
  // CLIENT ONBOARDING
  // ============================================
  "when-to-use-post-booking-mode": {
    id: "when-to-use-post-booking-mode",
    title: "When to use post-booking mode",
    category: "client-onboarding",
    readTime: "3 min",
    content: `
## When to Use Post-Booking Mode

Post-booking mode is designed for clients who have already committed to working with you.

### Pre-Booking vs Post-Booking

| Pre-Booking | Post-Booking |
|-------------|--------------|
| Leads you're evaluating | Clients you've won |
| Focus: Qualification | Focus: Onboarding |
| AI: Lead scoring | AI: Clarity scoring |
| Goal: Decide if it's a fit | Goal: Gather project details |

### Use Post-Booking When:

1. **Contract is signed** — They've committed
2. **Payment received** — Deposit or first invoice paid
3. **Verbal agreement** — Handshake deal confirmed
4. **Referred client** — Warm intro, ready to start

### Don't Use Post-Booking For:

- Initial inquiries (use pre-booking)
- Quote requests (use pre-booking)
- People "just looking" (use pre-booking)
- Leads you haven't qualified yet

### The Post-Booking Workflow

1. **Client commits** — Signs contract or pays deposit
2. **Send intake link** — Your post-booking form URL
3. **Client completes** — Fills out project details
4. **Review submission** — Check completeness
5. **Begin work** — Start with full context

### Benefits of Separate Modes

- **Better data** — AI optimized for each stage
- **Clearer pipeline** — Leads separate from clients
- **Right questions** — Form fields match the stage
- **Accurate metrics** — Track leads and intakes separately

---

**Example:** Agency sends pre-booking form to website visitors. Once a visitor becomes a client, they're sent a post-booking intake form for project onboarding.
    `
  },

  "creating-post-booking-intake-form": {
    id: "creating-post-booking-intake-form",
    title: "Creating a post-booking intake form",
    category: "client-onboarding",
    readTime: "4 min",
    content: `
## Creating a Post-Booking Intake Form

Build an intake form specifically for onboarding new clients.

### Creating the Form

1. Go to **Form Builder**
2. Create a new form or duplicate an existing one
3. Set **Form Mode** to "Post-Booking"
4. Customize fields for client onboarding

### Recommended Fields

#### Essential Info
- Full Name
- Email
- Company Name
- Phone Number

#### Project Details
- Project Description (detailed)
- Project Goals
- Target Audience
- Timeline/Deadlines
- Budget Confirmation

#### Requirements
- Specific Deliverables Needed
- Technical Requirements
- Style Preferences
- Reference Examples

#### Administrative
- Preferred Communication Method
- Key Stakeholders/Contacts
- Billing Contact (if different)

### Enabling Agreement & Documents

For post-booking forms, consider requiring:
- **Agreement acknowledgment** — Confirm they accept terms
- **Document uploads** — Collect assets upfront

Enable these in the form settings.

### Form Welcome Message

Customize the welcome message for clients:

> "Welcome to [Your Company]! Please complete this intake form so we can hit the ground running. This should take about 10 minutes."

### Testing Your Form

1. Save the form
2. Copy the form link
3. Open in incognito/private browser
4. Complete as if you were a client
5. Check submission appears correctly

---

**Tip:** Post-booking forms can be longer than pre-booking forms since clients are already committed.
    `
  },

  "sending-intake-requests-to-clients": {
    id: "sending-intake-requests-to-clients",
    title: "Sending intake requests to clients",
    category: "client-onboarding",
    readTime: "3 min",
    content: `
## Sending Intake Requests to Clients

After a client commits, send them your intake form using HeyIntake's secure intake request system.

### Two Ways to Start Intake

#### 1. From a Won Lead (Recommended)
When you mark a lead as Won:
1. A modal appears with **"Send intake email now"** toggle (default ON)
2. Select which intake form to use
3. Confirm the recipient email
4. Click confirm — email is sent automatically

#### 2. Intake-First (New Client)
For clients who didn't come through lead capture:
1. Click **"+ Start Intake"** on the Submissions page
2. Enter client name and email (or select existing contact)
3. Choose an intake form
4. Toggle whether to send email immediately
5. Submit

### Secure Intake Links

Every intake request generates a **secure token**:
- 256-bit cryptographically secure (not guessable)
- **Expires after 14 days** for security
- **One-time use** — can't be reused after submission
- Previous tokens are **invalidated** when you resend

Your client receives a link like:
\`\`\`
https://heyintake.com/form/abc123?token=secure-token-here
\`\`\`

### What Happens When Client Submits

1. Token is validated (checks expiration, usage)
2. Their submission updates the **same record** (no duplicates)
3. Status changes: Intake Requested → Intake Received
4. **You receive an email notification** automatically
5. Activity Timeline logs the submission

### Resending Intake Emails

If a client loses their link or it expires:
1. Open the submission
2. Click **"Resend Intake Email"**
3. A **new secure token** is generated
4. Previous token is automatically invalidated

### Tracking Status

| Status | What It Means |
|--------|---------------|
| Awaiting Intake | Won but intake not sent yet |
| Intake Requested | Email sent, waiting for client |
| Intake Received | Client submitted successfully |

### Dual Status Visibility

After sending intake, submissions show both:
- **Lead Stage:** Won (for conversion tracking)
- **Intake Stage:** Intake Requested (for onboarding tracking)

Both are visible on cards and in the submission modal.

---

**Security Note:** Intake tokens are stored as SHA-256 hashes in our database. The raw token is only sent to the client and never stored, ensuring maximum security.
    `
  },

  "managing-incomplete-intakes": {
    id: "managing-incomplete-intakes",
    title: "Managing incomplete intakes",
    category: "client-onboarding",
    readTime: "3 min",
    content: `
## Managing Incomplete Intakes

Some clients need reminders or help completing their intake.

### Identifying Incomplete Intakes

Check for:
- **Intake Requested** status — Sent but not completed
- **Low clarity score** — Submitted but missing info
- **Missing documents** — Files not uploaded

### Why Intakes Go Incomplete

Common reasons:
- Client is busy/distracted
- Form is too long
- Technical issues
- Unclear what's needed
- Waiting on information

### Follow-Up Strategy

#### Day 3: Gentle Reminder
> "Hi [Name], just a reminder about the intake form. Let me know if you have questions!"

#### Day 7: Value Reminder
> "Want to make sure we have everything to start strong. The intake form takes ~10 min: [link]"

#### Day 14: Direct Call
If still incomplete, call to:
- Offer to complete together
- Identify what's blocking them
- Simplify if needed

### Handling Low Clarity Scores

If submitted but score is low:
1. Review the AI-flagged gaps
2. Send specific follow-up questions
3. Offer a quick call to fill in details

### Client Action Required

Mark submissions needing client input:
1. Open the submission
2. Note what's missing
3. Reach out directly
4. Update status when resolved

### Preventing Incomplete Intakes

- **Shorten forms** — Remove non-essential questions
- **Clear instructions** — Explain what's needed
- **Set expectations** — "This takes 10 minutes"
- **Offer help** — "Happy to hop on a call to complete together"

---

**Pro Tip:** A 5-minute call often resolves more than multiple reminder emails.
    `
  },

  "intake-reminder-automations": {
    id: "intake-reminder-automations",
    title: "Intake reminder automations",
    category: "client-onboarding",
    readTime: "3 min",
    content: `
## Intake Reminder Automations

Automatically remind clients who haven't completed their intake.

### How Automated Reminders Work

When enabled:
1. Client receives intake link
2. If not completed within X days, reminder sends automatically
3. Continues until completed or disabled

### Enabling Reminders

1. Go to **Settings**
2. Find **Notifications** section
3. Enable **Intake Reminders**
4. Configure timing (e.g., every 3 days)

### Reminder Timing Options

| Option | Best For |
|--------|----------|
| After 3 days | Standard projects |
| After 7 days | Less urgent work |
| Custom | Your preference |

### What Reminders Include

Automated reminder emails contain:
- Friendly reminder message
- Link to complete the intake
- Your business name/branding

### Manual vs Automatic

| Manual Reminders | Automatic Reminders |
|------------------|---------------------|
| Personal touch | Hands-off |
| Custom message | Standard template |
| One-time | Recurring |
| Requires action | Set and forget |

### When to Disable

Turn off auto-reminders if:
- You prefer manual follow-up
- The project timeline changed
- Client communicated delay

### Complementing Automation

Even with automation, consider:
- Personal check-in after 2 reminders
- Phone call if still incomplete
- Adjusting intake form if many incomplete

---

**Note:** Clients can complete the intake at any time to stop reminders automatically.
    `
  },

  // ============================================
  // NOTIFICATIONS
  // ============================================
  "email-notification-settings": {
    id: "email-notification-settings",
    title: "Email notification settings",
    category: "notifications",
    readTime: "2 min",
    content: `
## Email Notification Settings

Control what emails you receive from HeyIntake.

### Accessing Settings

1. Go to **Settings** in the sidebar
2. Find **Email Notifications** section
3. Toggle options on/off

### Available Notifications

| Notification | Description |
|--------------|-------------|
| New Submission | Alert when form is submitted |
| Reminder Notifications | Updates about due reminders |
| Email Digest | Daily/weekly summary |

### New Submission Alerts

When enabled, you receive an email with:
- Lead/intake summary
- AI score and priority
- Key submission details
- Link to view full details

### Notification Email Address

Notifications go to the email in your Settings. To change:
1. Go to Settings > Business Profile
2. Update "Notification Email"
3. Save changes

### Turning Off Notifications

If inbox is overwhelming:
- Disable individual notification types
- Use digest instead of instant alerts
- Focus on dashboard checking instead

### Team Notifications

If you have team members:
- Each member controls their own notifications
- Business owner sets default notification email
- Important alerts go to team as appropriate

---

**Tip:** Start with all notifications on, then adjust based on volume and preference.
    `
  },

  "new-submission-alerts": {
    id: "new-submission-alerts",
    title: "New submission alerts",
    category: "notifications",
    readTime: "2 min",
    content: `
## New Submission Alerts

Get instant notifications when someone submits your form.

### What's Included

Each alert contains:
- **Submitter info** — Name, email, company
- **AI Summary** — Key points at a glance
- **Score & Priority** — Know urgency instantly
- **Quick link** — Jump to full details

### Email Format

Subject: "New Lead: [Name] - [Company]" or "New Intake: [Name]"

Body includes:
- Formatted summary
- Score badge
- Top form responses
- Link to dashboard

### Response Time Matters

Fast response increases conversion:
- < 5 minutes = 9x more likely to convert
- 5-30 minutes = 4x more likely
- > 1 hour = odds drop significantly

Use alerts to respond quickly to high-priority leads.

### Managing Alert Volume

If you get many submissions:
- Focus on high-score alerts
- Check others in dashboard
- Consider digest for low-priority forms

### Mobile Alerts

Emails work on mobile, so you can:
- See new leads on the go
- Quickly assess priority
- Respond or forward as needed

---

**Pro Tip:** Set up email filters to highlight high-priority alerts in your inbox.
    `
  },

  "setting-up-reminders": {
    id: "setting-up-reminders",
    title: "Setting up reminders",
    category: "notifications",
    readTime: "3 min",
    content: `
## Setting Up Reminders

Never forget to follow up with manual reminders on leads and intakes.

### Creating a Reminder

1. Open any submission details
2. Find **Reminders** section
3. Click **Add Reminder**
4. Set date/time
5. Add optional note
6. Save

### Quick Reminder Options

- **Tomorrow** — Follow up next day
- **In 3 days** — Standard follow-up window
- **Next week** — Longer-term follow-up
- **Custom** — Any date you choose

### What to Include

Good reminder notes specify:
- What action to take
- Any context needed
- Why this timing matters

Examples:
- "Call to discuss proposal"
- "Send contract if no response"
- "Check if they received materials"

### Viewing Reminders

Find your reminders:
- Dashboard shows upcoming reminders
- Each submission shows its reminders
- Filter submissions by "Has Reminder"

### Completing Reminders

When you've followed up:
1. Open the submission
2. Find the reminder
3. Mark as complete or reschedule

### Reminder Best Practices

- Set reminder immediately after interaction
- Use specific action notes
- Don't over-remind (overwhelming)
- Review and clean up old reminders

---

**Tip:** Build a habit of setting the next reminder before closing any lead.
    `
  },

  "automated-intake-reminders": {
    id: "automated-intake-reminders",
    title: "Automated intake reminders",
    category: "notifications",
    readTime: "3 min",
    content: `
## Automated Intake Reminders

Let HeyIntake automatically remind clients to complete their intake forms.

### How It Works

1. Client receives intake form link
2. System tracks if they've submitted
3. After X days without submission, reminder sends
4. Continues until they complete or you disable

### Enabling Auto-Reminders

1. Go to **Settings**
2. Find **Reminder Notifications** toggle
3. Enable for intake forms
4. Auto-reminders activate

### Reminder Schedule

Default behavior:
- First reminder: 3 days after sending
- Subsequent: Every few days
- Stops when intake is submitted

### What Clients Receive

Reminder email includes:
- Friendly nudge to complete
- Link to the intake form
- Your business name

### Customization Options

Depending on your plan:
- Adjust reminder frequency
- Customize reminder message (coming soon)
- Set maximum number of reminders

### When to Use Auto-Reminders

Good for:
- High-volume client onboarding
- Standardized intake processes
- Reducing manual follow-up work

Not ideal for:
- VIP clients (personal touch better)
- Complex projects (call instead)
- Sensitive situations

---

**Note:** Clients can always reach out if they have questions about the intake form.
    `
  },

  // ============================================
  // BILLING & PLANS
  // ============================================
  "understanding-your-trial": {
    id: "understanding-your-trial",
    title: "Understanding your trial",
    category: "billing",
    readTime: "2 min",
    content: `
## Understanding Your Trial

Try HeyIntake risk-free with our 7-day trial.

### What's Included

Your trial gives you:
- **7 days** of full access
- **20 submissions** included
- All features unlocked
- No feature restrictions

### Trial Activation

- **$1 preview charge** — Verifies payment method
- Full amount charged after 7 days
- Cancel anytime before trial ends

### During Your Trial

You can:
- Create and customize forms
- Receive and manage submissions
- Use AI scoring and summaries
- Set up team members
- Configure all settings

### Trial Limits

- **20 submissions max** — Additional submissions available after upgrade
- **7 days** — Trial ends automatically

### After Trial Ends

If you don't cancel:
- Subscription begins at selected plan rate
- All your data and settings remain
- No interruption to service

If you cancel:
- No further charges
- Account remains accessible (read-only)
- Can reactivate anytime

### Extending Your Trial

Need more time? Contact support to discuss options.

---

**Tip:** Use your trial to test with real leads so you can evaluate actual results.
    `
  },

  "plan-comparison": {
    id: "plan-comparison",
    title: "Plan comparison (Starter vs Pro)",
    category: "billing",
    readTime: "3 min",
    content: `
## Plan Comparison: Starter vs Professional

Choose the plan that fits your business needs.

### Quick Comparison

| Feature | Starter ($19/mo) | Professional ($49/mo) |
|---------|------------------|----------------------|
| Forms | 1 | 5 |
| Submissions | 100/month | 500/month |
| Pre & Post-booking | ✓ | ✓ |
| AI Scoring | ✓ | ✓ |
| CRM & Pipeline | ✓ | ✓ |
| Agreements | ✓ | ✓ |
| Document Links | ✓ | ✓ |
| CSV Export | ✓ | ✓ |
| Custom Branding | - | ✓ |
| Remove Badge | - | ✓ |
| Team Members | - | ✓ |
| Email Support | - | ✓ |

### Starter Plan ($19/month)

Best for:
- Solo consultants
- Freelancers
- Single-service businesses
- Getting started

Includes:
- 1 intake form
- 100 submissions/month
- Full AI features
- All core functionality

### Professional Plan ($49/month)

Best for:
- Growing businesses
- Multiple service lines
- Teams
- Higher volume

Includes:
- 5 intake forms
- 500 submissions/month
- Everything in Starter
- Branding customization
- Team collaboration
- Priority support

### Enterprise

For high-volume or custom needs:
- Unlimited forms and submissions
- White-label options
- Custom integrations
- Dedicated support

Contact sales for pricing.

### Annual Discount

Save 20% by paying annually:
- Starter: $16/month (billed $192/year)
- Professional: $39/month (billed $468/year)

---

**Recommendation:** Start with Starter, upgrade to Professional as you grow.
    `
  },

  "upgrading-your-plan": {
    id: "upgrading-your-plan",
    title: "Upgrading your plan",
    category: "billing",
    readTime: "2 min",
    content: `
## Upgrading Your Plan

Upgrade anytime to unlock more features and capacity.

### How to Upgrade

1. Go to **Settings** in the sidebar
2. Find the **Billing** section
3. View available plans
4. Click **Upgrade** on your desired plan
5. Confirm payment

### What Happens When You Upgrade

- **Immediate access** — New features unlock instantly
- **Prorated billing** — You only pay the difference
- **No data loss** — Everything transfers automatically
- **Same account** — No re-setup required

### Upgrade Scenarios

#### Starter → Professional
- Forms increase from 1 to 5
- Submissions increase from 100 to 500
- Team members unlocked
- Branding options available

#### Monthly → Annual
- Same features
- 20% discount applied
- Billed yearly instead of monthly

### Mid-Cycle Upgrades

If you upgrade mid-billing cycle:
- Prorated credit for unused time
- New plan starts immediately
- Next bill reflects new plan

### Can't Decide?

Consider upgrading if:
- You need more forms
- Hitting submission limits
- Want team collaboration
- Need branding customization

---

**Tip:** Upgrade before you hit limits to avoid any interruption.
    `
  },

  "managing-payment-methods": {
    id: "managing-payment-methods",
    title: "Managing payment methods",
    category: "billing",
    readTime: "2 min",
    content: `
## Managing Payment Methods

Update your payment information securely.

### Accessing Payment Settings

1. Go to **Settings**
2. Click **Billing** section
3. Find **Payment Method**

### Updating Your Card

1. Click **Update Payment Method**
2. Enter new card details
3. Save changes
4. Old card is replaced

### Accepted Payment Methods

- Credit cards (Visa, Mastercard, Amex)
- Debit cards
- Payments processed securely via Stripe

### When Cards Are Charged

- **Trial start**: $1 verification charge
- **Trial end**: First full payment
- **Renewals**: Same day each month/year

### Failed Payments

If a payment fails:
1. You'll receive an email notification
2. We'll retry a few times
3. Update card to prevent interruption
4. Account may pause if unresolved

### Payment Security

- All payments handled by Stripe
- Card details never stored on our servers
- PCI-compliant infrastructure
- Encrypted transactions

### Receipts & Invoices

Find payment history:
1. Go to Settings > Billing
2. View past invoices
3. Download for records

---

**Need help?** Contact support if you have billing questions.
    `
  },

  "canceling-your-subscription": {
    id: "canceling-your-subscription",
    title: "Canceling your subscription",
    category: "billing",
    readTime: "2 min",
    content: `
## Canceling Your Subscription

You can cancel your subscription at any time.

### How to Cancel

1. Go to **Settings**
2. Click **Billing** section
3. Click **Cancel Subscription**
4. Confirm cancellation
5. Access continues until period ends

### What Happens After Cancellation

- **Immediate**: Cancellation confirmed
- **Until period ends**: Full access continues
- **After period ends**: Account becomes read-only
- **Your data**: Remains accessible, not deleted

### Canceling During Trial

If you cancel during your 7-day trial:
- No further charges
- $1 preview charge is not refunded
- Access until trial period ends

### Reactivating Later

Changed your mind? You can:
1. Return to Settings > Billing
2. Choose a plan
3. Reactivate immediately
4. All your data is still there

### Before You Cancel

Consider:
- **Downgrading** instead of canceling
- **Pausing** if you need a break (contact support)
- **Feedback** — We'd love to know why

### Data After Cancellation

Your data is retained:
- Forms and settings preserved
- Submission history accessible
- Can export data anytime
- Reactivation restores full access

---

**Questions?** Contact support before canceling — we may be able to help.
    `
  },

  // ============================================
  // ACCOUNT SETTINGS
  // ============================================
  "updating-business-info": {
    id: "updating-business-info",
    title: "Updating business info",
    category: "settings",
    readTime: "2 min",
    content: `
## Updating Business Info

Keep your business profile current for forms and communications.

### Accessing Business Settings

1. Go to **Settings** in the sidebar
2. Find **Business Profile** section

### What You Can Update

#### Business Name
- Appears on your intake forms
- Shown in email notifications
- Used in client communications

#### Notification Email
- Where lead alerts are sent
- Separate from login email if needed
- Must be a valid email address

### Making Changes

1. Edit the field you want to change
2. Click **Save** or **Update**
3. Changes apply immediately

### Where Business Info Appears

- Form headers and footers
- Email notifications to you
- Any client-facing communications

### Tips

- Use your professional business name
- Keep notification email current
- Update if you rebrand

---

**Note:** Changing business name doesn't affect past submissions or records.
    `
  },

  "adding-scheduling-links": {
    id: "adding-scheduling-links",
    title: "Adding scheduling links",
    category: "settings",
    readTime: "3 min",
    content: `
## Adding Scheduling Links

Include your calendar link in automated emails.

### Why Add Scheduling Links

When leads want to meet, they can:
- Book directly from your email
- See your availability
- Reduce back-and-forth

### Supported Schedulers

Add links from:
- **Calendly** — calendly.com/yourname
- **Google Calendar** — calendar.google.com/...
- **Square Appointments** — squareup.com/appointments/...
- **Any scheduling tool** with a public link

### Adding Your Link

1. Go to **Settings**
2. Find **Scheduling Links** section
3. Paste your scheduling URL
4. Save changes

### Where Links Appear

Your scheduling link is included in:
- "Schedule a Call" emails (if sent)
- Automated follow-up templates
- Can be referenced in manual emails

### Multiple Calendars

If you use different calendars:
- Choose primary for HeyIntake
- Include others in custom communications
- Or use one consolidated booking page

### Testing Your Link

1. Copy your scheduling link
2. Open in incognito browser
3. Verify booking page loads
4. Check availability displays correctly

---

**Tip:** Include your scheduling link in your email signature for easy access.
    `
  },

  "configuring-agreement-settings": {
    id: "configuring-agreement-settings",
    title: "Configuring agreement settings",
    category: "settings",
    readTime: "3 min",
    content: `
## Configuring Agreement Settings

Set up your agreement link for client acknowledgments.

### Accessing Agreement Settings

1. Go to **Settings**
2. Scroll to **Agreement Handling** section

### What to Configure

#### Agreement Link
- URL to your agreement document
- Must be publicly accessible
- Starts with https://

#### Instructions
- Brief text shown to clients
- Explains what they're agreeing to
- Up to 500 characters

### Setting Up

1. **Prepare your agreement document**
   - Upload to accessible location (Google Drive, website, etc.)
   - Copy the public URL

2. **Add to HeyIntake**
   - Paste URL in Agreement Link field
   - Write clear instructions
   - Save changes

3. **Enable on forms**
   - Go to Form Builder
   - Enable "Require Agreement" on relevant forms

### Link Format Examples

- PDF: \`https://yourdomain.com/agreement.pdf\`
- Google Doc: \`https://docs.google.com/document/d/xxx/view\`
- DocuSign: \`https://docusign.com/xxx\`

### Testing

1. Enable agreement on a test form
2. Open form in incognito
3. Click "Review Agreement"
4. Verify document opens correctly

---

**Important:** Update your Settings if you change your agreement document location.
    `
  },

  "document-handling-options": {
    id: "document-handling-options",
    title: "Document handling options",
    category: "settings",
    readTime: "3 min",
    content: `
## Document Handling Options

Configure how clients submit documents for their projects.

### Accessing Document Settings

1. Go to **Settings**
2. Find **Document Handling** section

### Available Options

#### 1. None
- No document upload required
- Simplest option
- Use for forms that don't need files

#### 2. External Upload Link
- You provide a Dropbox/Drive link
- Clients upload to your storage
- Most flexible option

#### 3. Business Email
- Clients email documents to you
- Your email displayed in form
- Good for occasional files

### Configuring External Upload

1. Create your upload destination (Dropbox, Drive, etc.)
2. Get the shareable link
3. Paste in "External Upload Link" field
4. Add instructions for clients
5. Save settings

### Configuring Email Option

1. Select "Business Email" option
2. Confirm your notification email
3. Clients will be instructed to email files

### Instructions Field

Write clear instructions:
- What files to upload
- Preferred formats
- Any naming conventions
- Deadline if applicable

### Which Option to Choose?

| Situation | Recommendation |
|-----------|----------------|
| Regular document collection | External Upload Link |
| Occasional files | Business Email |
| No files needed | None |

---

**Tip:** Test your document flow by submitting a test form yourself.
    `
  }
};

// Helper function to get article by slug
export const getArticleBySlug = (slug) => {
  return docsContent[slug] || null;
};

// Helper function to get all articles for a category
export const getArticlesByCategory = (categoryId) => {
  return Object.values(docsContent).filter(article => article.category === categoryId);
};

// Export category metadata
export const docCategories = [
  { id: "getting-started", title: "Getting Started", icon: "Zap" },
  { id: "crm-pipeline", title: "CRM & Pipeline", icon: "Briefcase" },
  { id: "ai-scoring", title: "AI Lead Scoring", icon: "Brain" },
  { id: "agreements", title: "Agreement Acknowledgment", icon: "FileCheck" },
  { id: "documents", title: "Document Collection", icon: "Upload" },
  { id: "client-onboarding", title: "Client Onboarding", icon: "UserCheck" },
  { id: "notifications", title: "Notifications & Reminders", icon: "Bell" },
  { id: "billing", title: "Billing & Plans", icon: "CreditCard" },
  { id: "settings", title: "Account Settings", icon: "Settings" }
];
